import { useState } from 'react';
import { Search, Loader2, BookOpen, ListChecks, BookMarked, Library, Network } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import { AuthDialog } from '@/components/auth/auth-dialog';
import { useAuth } from '@/lib/auth/context';

export function VideoInput() {
  const [url, setUrl] = useState('');
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isLaunched] = useState(import.meta.env.VITE_IS_LAUNCHED === 'true');
  const [isValidUrl, setIsValidUrl] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [generateOptions, setGenerateOptions] = useState({
    generateNotes: true,
    generateMCQ: true,
    generateGlossary: true,
    generateFlashcards: true,
    generateMindmap: true
  });

  const cleanYouTubeUrl = (inputUrl: string) => {
    const url = inputUrl.trim();
    console.log('Original URL:', url);
    
    // Handle youtu.be format
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      console.log('Extracted Video ID:', videoId);
      if (videoId) {
        const cleanUrl = `https://youtube.com/watch?v=${videoId}`;
        console.log('Transformed URL:', cleanUrl);
        return cleanUrl;
      }
    }
    
    console.log('Unchanged URL:', url);
    return url;
  };

  const handleConvert = () => {
    if (!user) {
      setIsAuthOpen(true);
      return;
    }

    if (url) {
      const cleanUrl = cleanYouTubeUrl(url);
      navigate('/newoutput', { state: { youtubeUrl: cleanUrl } });
    }
  };

  const handleCheckboxChange = (option: string) => (checked: boolean) => {
    setGenerateOptions(prev => ({ ...prev, [option]: checked }));
  };

  const validateYoutubeUrl = (url: string): boolean => {
    // Check if URL is empty
    if (!url) return false;
    
    // Handle both youtube.com and youtu.be formats
    const regex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[a-zA-Z0-9_-]{11}/;
    return regex.test(url);
  };

  return (
    <div className="relative space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center justify-center text-red-500">
        <Network className="h-8 w-8" />
      </div>
      
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          placeholder="Paste YouTube URL to begin..."
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-[#2D7FFF] dark:focus:ring-[#1A3C75] text-base transition-all duration-200"
          value={url}
          onChange={(e) => {
            const inputUrl = e.target.value;
            setUrl(inputUrl);
            setIsValidUrl(validateYoutubeUrl(inputUrl));
          }}
        />
      </div>

      <AnimatePresence>
        {isValidUrl && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
              <motion.div 
                className="flex flex-col gap-3"
                transition={{ staggerChildren: 0.1 }}
              >
                {Object.entries(generateOptions).map(([key, value], index) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <Checkbox
                        checked={value}
                        onCheckedChange={handleCheckboxChange(key)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-[#2D7FFF] dark:group-hover:text-[#60A5FA] transition-colors">
                        {key.replace('generate', '').replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </label>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        onClick={handleConvert}
        disabled={!isValidUrl}
        className="w-full py-3 text-base transition-all duration-200"
      >
        Try Now
      </Button>

      <AuthDialog
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
      />
    </div>
  );
}